/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import "../../core/has.js";
import { loadArcade as e } from "../../support/arcadeOnDemand.js";
import { getStroke as t } from "./gfxUtils.js";
import { SymbolSizeDefaults as l } from "./previewUtils.js";
import { renderSymbol as i, renderOnce as r } from "./renderUtils.js";
import { getCSSFilterFromEffectList as a, fetchWebStyleSymbol as s, applyColorToSymbol as n, applySizesToSymbol as o, applyRotationToSymbol as c, getColorFromSymbol as u, applyOpacityToColor as f } from "./utils.js";
import { renderRelationshipRamp as p } from "../../widgets/Legend/styles/support/relationshipUtils.js";
import { getRelationshipRampElement as h } from "../../widgets/Legend/support/relationshipRampUtils.js";
let y = null;
const d = [255, 255, 255];
function m(e, t) {
  return Math.floor(Math.random() * (t - e + 1) + e);
}
function b(e, t, i) {
  const {
      backgroundColor: r,
      outline: a,
      dotSize: s
    } = e,
    n = i?.swatchSize || l.size,
    o = .8,
    c = Math.round(n * n / s ** 2 * o),
    u = window.devicePixelRatio,
    f = document.createElement("canvas"),
    p = n * u;
  f.width = p, f.height = p, f.style.width = f.width / u + "px", f.style.height = f.height / u + "px";
  const h = f.getContext("2d");
  if (r && (h.fillStyle = r.toCss(!0), h.fillRect(0, 0, p, p), h.fill()), h.fillStyle = t?.toCss(!0) ?? "", y && y.length / 2 === c) for (let l = 0; l < 2 * c; l += 2) {
    const e = y[l],
      t = y[l + 1];
    h.fillRect(e, t, s * u, s * u), h.fill();
  } else {
    y = [];
    for (let e = 0; e < 2 * c; e += 2) {
      const e = m(0, p),
        t = m(0, p);
      y.push(e, t), h.fillRect(e, t, s * u, s * u), h.fill();
    }
  }
  a && (a.color && (h.strokeStyle = a.color.toCss(!0)), h.lineWidth = a.width, h.strokeRect(0, 0, p, p));
  const d = new Image(n, n);
  return d.src = f.toDataURL(), d.ariaLabel = i?.ariaLabel ?? null, d.alt = i?.ariaLabel ?? "", d;
}
function g(e, l = {}) {
  const r = l.radius || 40,
    a = 2 * Math.PI * r,
    s = e.length,
    n = a / s,
    o = [],
    c = t(l.outline);
  null != c?.width && (c.width *= 2), (c || l.backgroundColor) && o.push({
    shape: {
      type: "circle",
      cx: r,
      cy: r,
      r
    },
    fill: l.backgroundColor,
    stroke: c,
    offset: [0, 0]
  });
  const u = l.values,
    f = u && u.length === s && 100 === u.reduce((e, t) => e + t, 0),
    p = [0];
  for (let t = 0; t < s; t++) {
    let l = null;
    f && (l = u[t] * a / 100, p.push(l + p[t])), o.push({
      shape: {
        type: "circle",
        cx: r,
        cy: r,
        r: r / 2
      },
      fill: [0, 0, 0, 0],
      stroke: {
        width: r,
        dashArray: `${(l ?? n) / 2} ${a}`,
        dashoffset: "-" + (f ? p[t] / 2 : t * (n / 2)),
        color: e[t]
      },
      offset: [0, 0]
    });
  }
  let h = null;
  const y = 2 * r + (c?.width || 0),
    m = l.holePercentage;
  if (m) {
    c && o.push({
      shape: {
        type: "circle",
        cx: r,
        cy: r,
        r: r * m
      },
      fill: null,
      stroke: c,
      offset: [0, 0]
    });
    const e = y / 2;
    h = [[{
      shape: {
        type: "circle",
        cx: e,
        cy: e,
        r: e
      },
      fill: d,
      stroke: c ? {
        ...c,
        color: d
      } : null,
      offset: [0, 0]
    }, {
      shape: {
        type: "circle",
        cx: e,
        cy: e,
        r: r * m
      },
      fill: [0, 0, 0],
      stroke: null,
      offset: [0, 0]
    }]];
  }
  return i([o], [y, y], {
    effectView: l.effectList,
    ignoreStrokeWidth: !0,
    masking: h,
    rotation: -90,
    ariaLabel: l.ariaLabel
  });
}
function w(e, t = {}) {
  const l = e?.authoringInfo;
  if (!("relationship" === l?.type) || !l?.numClasses || !e.uniqueValueInfos) return null;
  const {
      focus: i,
      numClasses: a
    } = l,
    s = h({
      focus: i,
      numClasses: a,
      infos: e.uniqueValueInfos
    }),
    n = t?.node || document.createElement("div");
  return r(n, () => p(s, t.id || "relationship", {
    opacity: t.opacity || 1,
    effectList: t.effectList,
    ariaLabel: t.ariaLabel
  })), n;
}
function v(e, t = {}) {
  const l = 24,
    i = 75,
    r = "horizontal" === t.align,
    a = r ? i : l,
    s = r ? l : i,
    n = t.width ?? a,
    o = t.height ?? s,
    c = t.gradient ?? !0,
    u = window.devicePixelRatio,
    f = n * u,
    p = o * u,
    h = document.createElement("canvas");
  h.width = f, h.height = p, h.ariaLabel = t.ariaLabel ?? null, h.style.width = `${n}px`, h.style.height = `${o}px`;
  const y = h.getContext("2d"),
    d = r ? f : 0,
    m = r ? 0 : p;
  if (c) {
    const t = y.createLinearGradient(0, 0, d, m),
      l = e.length,
      i = 1 === l ? 0 : 1 / (l - 1);
    e.forEach((e, l) => t.addColorStop(l * i, e.toString())), y.fillStyle = t, y.fillRect(0, 0, f, p);
  } else {
    const t = r ? f / e.length : f,
      l = r ? p : p / e.length;
    let i = 0,
      a = 0;
    for (const s of e) y.fillStyle = s.toString(), y.fillRect(i, a, t, l), i = r ? i + t : 0, a = r ? 0 : a + l;
  }
  const b = document.createElement("div");
  return b.style.width = `${n}px`, b.style.height = `${o}px`, S(b, t?.effectList), b.appendChild(h), b;
}
function S(e, t) {
  if (!t) return;
  e.style.filter = a(t);
  const l = t.effects;
  if (l) for (const i of l) if ("drop-shadow" === i?.type) {
    i.offsetX < 0 ? e.style.marginLeft = `${Math.abs(i.offsetX)}px` : e.style.marginRight = `${i.offsetX}px`;
    break;
  }
}
async function V(e, t) {
  switch (e.type) {
    case "web-style":
      {
        const {
          previewWebStyleSymbol: l
        } = await import("./previewWebStyleSymbol.js");
        return l(e, V, t);
      }
    case "label-3d":
    case "line-3d":
    case "mesh-3d":
    case "point-3d":
    case "polygon-3d":
      {
        const {
          previewSymbol3D: l
        } = await import("./previewSymbol3D.js");
        return l(e, t);
      }
    case "simple-marker":
    case "simple-line":
    case "simple-fill":
    case "picture-marker":
    case "picture-fill":
    case "text":
      {
        const {
          previewSymbol2D: l
        } = await import("./previewSymbol2D.js");
        return l(e, t);
      }
    case "cim":
      {
        const {
          previewCIMSymbol: l
        } = await import("./previewCIMSymbol.js");
        return l(e, t);
      }
    default:
      return;
  }
}
function x(e) {
  return e && "opacity" in e ? e.opacity * x(e.parent) : 1;
}
async function L(t, l) {
  if (!t) return;
  const i = t.sourceLayer,
    r = (null != l && l.useSourceLayer ? i : t.layer) ?? i,
    a = x(r);
  if (null != t.symbol && (null == l || !0 !== l.ignoreGraphicSymbol)) {
    const e = "web-style" === t.symbol.type ? await s(t.symbol, {
      ...l,
      cache: null != l ? l.webStyleCache : null
    }) : t.symbol.clone();
    return n(e, null, a), e;
  }
  const u = (null != l ? l.renderer : null) ?? (r && "renderer" in r ? r.renderer : null);
  let f = u && "getSymbolAsync" in u ? await u.getSymbolAsync(t, l) : null;
  if (!f) return;
  if (f = "web-style" === f.type ? await f.fetchSymbol({
    ...l,
    cache: null != l ? l.webStyleCache : null
  }) : f.clone(), !u || !("visualVariables" in u) || !u.visualVariables?.length) return n(f, null, a), f;
  if ("arcadeRequiredForVisualVariables" in u && u.arcadeRequiredForVisualVariables && null == l?.arcade) {
    const t = {
      ...l
    };
    t.arcade = await e(), l = t;
  }
  const {
      getColor: p,
      getOpacity: h,
      getAllSizes: y,
      getRotationAngle: d
    } = await import("../../renderers/visualVariables/support/visualVariableUtils.js"),
    m = [],
    b = [],
    g = [],
    w = [];
  for (const e of u.visualVariables) switch (e.type) {
    case "color":
      m.push(e);
      break;
    case "opacity":
      b.push(e);
      break;
    case "rotation":
      w.push(e);
      break;
    case "size":
      e.target || g.push(e);
  }
  const v = !!m.length && m[m.length - 1],
    S = v ? p(v, t, l) : null,
    V = !!b.length && b[b.length - 1];
  let L = V ? h(V, t, l) : null;
  if (null != a && (L = null != L ? L * a : a), n(f, S, L), g.length) {
    const e = y(g, t, l);
    await o(f, e);
  }
  for (const e of w) c(f, d(e, t, l), e.axis);
  return f;
}
async function k(t, l) {
  if (!t) return;
  const i = t.layer,
    r = t.sourceLayer,
    a = x(i ?? r);
  if (null != t.symbol && (null == l || !0 !== l.ignoreGraphicSymbol)) {
    const e = "web-style" === t.symbol.type ? await s(t.symbol, l) : t.symbol.clone();
    return u(e, a);
  }
  const n = l?.renderer || (i && "renderer" in i ? i.renderer : void 0) || (r && "renderer" in r ? r.renderer : void 0);
  let o = n && "getSymbolAsync" in n ? await n.getSymbolAsync(t, l) : void 0;
  if (!o) return;
  o = "web-style" === o.type ? await s(o, l) : o.clone();
  const c = u(o, a);
  if (!n || !("visualVariables" in n) || "visualVariables" in n && !n.visualVariables || "visualVariables" in n && !n.visualVariables?.length) return c;
  if ("arcadeRequiredForVisualVariables" in n && n.arcadeRequiredForVisualVariables && null == l?.arcade) {
    const t = {
      ...l
    };
    t.arcade = await e(), l = t;
  }
  const {
      getColor: p,
      getOpacity: h
    } = await import("../../renderers/visualVariables/support/visualVariableUtils.js"),
    y = [],
    d = [];
  for (const e of n.visualVariables) switch (e.type) {
    case "color":
      y.push(e);
      break;
    case "opacity":
      d.push(e);
  }
  const m = y.length > 0 ? y[y.length - 1] : null,
    b = m ? p(m, t, l) : c,
    g = d.length > 0 ? d[d.length - 1] : null;
  let w = g ? h(g, t, l) : null;
  return null != a && (w = null != w ? w * a : a), b ? f(b, w) : null;
}
export { k as getDisplayedColor, L as getDisplayedSymbol, v as renderColorRampPreviewHTML, b as renderDotDensityPreviewHTML, g as renderPieChartPreviewHTML, V as renderPreviewHTML, w as renderRelationshipRampPreviewHTML };